import { useCallback } from "react";
import { useAuth } from "context/AuthContext";
import { useGetUserRole } from "features/Authentication/useGetUserRole";

export function useBuildUrlWithContext() {
  const { user } = useAuth();
  const { userRole } = useGetUserRole();

  const buildUrlWithContext = useCallback(
    (targetPath: string): string => {
      // Parse the target path to separate base path and existing params
      const [basePath, existingParamsString] = targetPath.split("?");
      const existingParams = new URLSearchParams(existingParamsString || "");

      // Check if user has switched to a subclient view
      const currentSelectedClient = sessionStorage.getItem(
        "currentSelectedClient",
      );
      const selectedClient = currentSelectedClient
        ? JSON.parse(currentSelectedClient)
        : null;

      // Get current URL params as fallback
      const currentParams = new URLSearchParams(window.location.search);

      // Determine clientId based on priority: selectedClient > URL params > user object
      const clientId =
        selectedClient?.clientId ||
        currentParams.get("clientId") ||
        user?.clientId?.toString() ||
        "";

      // Determine isClient based on useGetUserRole hook
      const isClient = userRole === "client" ? "true" : "false";

      const clientName =
        selectedClient?.clientName ||
        currentParams.get("clientName") ||
        user?.clientName ||
        "";

      const clientLogoUrl =
        selectedClient?.clientLogoUrl ||
        currentParams.get("clientLogoUrl") ||
        user?.clientLogo ||
        "";

      const loginUrl = currentParams.get("loginUrl") || "";

      // Only add params if we have client context (not for admin)
      if (!clientId || user?.role === "admin") {
        return targetPath;
      }

      // Merge existing params with client context params
      // Client context params take precedence if they exist
      const mergedParams = new URLSearchParams(existingParams);

      if (clientId) mergedParams.set("clientId", clientId);
      if (isClient) mergedParams.set("isClient", isClient);
      if (clientName) mergedParams.set("clientName", clientName);
      if (clientLogoUrl) mergedParams.set("clientLogoUrl", clientLogoUrl);
      if (loginUrl) mergedParams.set("loginUrl", loginUrl);

      const paramsString = mergedParams.toString();
      return paramsString ? `${basePath}?${paramsString}` : basePath;
    },
    [user, userRole],
  );

  return buildUrlWithContext;
}
